import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridDataStateChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';

import { Typography } from '@material-ui/core';
import { process, State } from '@progress/kendo-data-query';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ProductPackagesInfo,
  ProductPackageVersion,
} from '../../Interfaces/ProductsManagement/ProductPackagesInfo';
import { ApplicationState } from '../../store';
import {
  activatePackageVersion,
  getProductPackagesVersions,
} from '../../store/ProductsManagement/ProductsActions';
import { LoadingPanel } from '../LoadingPanel';
import { GetString } from '../StringResourceManager';

const ProductPackageVersionsGrid: React.FC = () => {
  const dispatch: Dispatch<any> = useDispatch();
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 20,
  });
  const [data, setData] = useState<ProductPackagesInfo[]>([]);

  const productPackagesData = useSelector(
    (state: ApplicationState) => state.productsReducer.productPackagesVersions,
  );
  const isFetchingProductPackages = useSelector(
    (state: ApplicationState) => state.productsReducer.isFetching,
  );

  useEffect(() => {
    getProductPackagesVersions(dispatch);
  }, []);

  useEffect(() => {
    setData(
      productPackagesData.map(item => ({
        ...item,
        SelectedVersion: item.AvailableVersions.find(
          v => v.Version === item.CurrentVersion,
        ),
      })),
    );
  }, [productPackagesData]);

  const refreshPackages = (): void => {
    getProductPackagesVersions(dispatch);
  };

  const handleVersionChange = (
    packageName: string,
    version: ProductPackageVersion,
  ): void => {
    setData(prev =>
      prev.map(item =>
        item.Name === packageName
          ? {
              ...item,
              SelectedVersion: version,
            }
          : item,
      ),
    );
  };

  const activateVersion = (item: ProductPackagesInfo) => {
    if (!item.SelectedVersion) {
      return;
    }
    activatePackageVersion(dispatch, item.SelectedVersion.ProductPackageId);
  };

  const VersionDropDownCell = (props: GridCustomCellProps): JSX.Element => {
    const item = props.dataItem as ProductPackagesInfo;
    return (
      <td>
        <DropDownList
          data={item.AvailableVersions}
          textField="Version"
          dataItemKey="ProductPackageId"
          value={item.SelectedVersion}
          onChange={e =>
            handleVersionChange(item.Name, e.value as ProductPackageVersion)
          }
        />
      </td>
    );
  };

  const ActivateButtonCell = (props: GridCustomCellProps): JSX.Element => {
    const item = props.dataItem as ProductPackagesInfo;

    return (
      <td>
        <Button
          themeColor="primary"
          disabled={
            !item.SelectedVersion ||
            item.SelectedVersion.Version === item.CurrentVersion
          }
          onClick={() => activateVersion(item)}
        >
          {GetString('ACTIVATE')}
        </Button>
      </td>
    );
  };

  return (
    <>
      {isFetchingProductPackages && LoadingPanel}
      <Grid
        style={{
          height: '100%',
          width: '100%',
          maxHeight: '600px',
          maxWidth: '1250px',
        }}
        sortable={true}
        filterable={true}
        resizable={true}
        pageable={{
          buttonCount: 4,
          pageSizes: [10, 20, 50, 100],
        }}
        data={process(data, dataState)}
        {...dataState}
        onDataStateChange={(e: GridDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
        dataItemKey="ProductPackageId"
      >
        <GridToolbar>
          <div
            style={{
              display: 'flex',
              gap: '96px',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {GetString('IMPORTED_PACKAGES')}
            </Typography>

            <Button type="submit" onClick={refreshPackages}>
              {GetString('REFRESH')}
            </Button>
          </div>
        </GridToolbar>

        <GridColumn
          field="Name"
          title={GetString('COLUMN_TITLE_PACKAGE_NAME')}
          width="250px"
        />
        <GridColumn
          field="CurrentVersion"
          title={GetString('COLUMN_TITLE_CURRENT_VERSION')}
          width="220px"
        />
        <GridColumn
          title={GetString('COLUMN_TITLE_AVAILABLE_VERSIONS')}
          width="250px"
          cells={{
            data: VersionDropDownCell,
          }}
        />
        <GridColumn
          title={GetString('ACTION_CATEGORY')}
          width="150px"
          cells={{
            data: ActivateButtonCell,
          }}
          filterable={false}
        />
      </Grid>
    </>
  );
};

export default ProductPackageVersionsGrid;
